import LabelLinked from '@/components/form/LabelLinked'
import TrustIndicator from '@/layout/TrustIndicator'
import fetchApi from '@/lib/tools/api'
import {normalizeString} from '@/lib/tools/utils'
import ContentSave from '@mui/icons-material/Save'
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import React, {Fragment, SyntheticEvent, useCallback, useEffect, useMemo, useState} from 'react'
import {RaRecord, useNotify, useSaveContext} from 'react-admin'
import {RiCloseCircleFill} from 'react-icons/ri'

export default function ProductLinked<TBaseEntity extends RaRecord, TLinkedEntity extends RaRecord>({
	                                                                                                    productId,
	                                                                                                    context,
	                                                                                                    enableTrust = false
                                                                                                    }: {
	productId: number,
	context: 'communes' | 'terroirs',
	enableTrust?: boolean
}) {

	const notify = useNotify()
	const {saving} = useSaveContext()

	const PARAMS = useMemo(() => {
		return {
			context: 'product',
			endpoints: {
				search: () => `/products`,
				getLinked: (id: number) => `/${context}/${id}/products`,
				save: (id: number) => `/${context}/${id}/products`,
				add: (id: number) => `/${context}/${id}/products/add`,
				delete: (id: number) => `/${context}/${id}/products/remove`
			}
		}
	}, [])

	const [filter, setFilter] = useState('')
	const [options, setOptions] = useState<TBaseEntity[]>([])
	const [touched, setTouched] = useState<boolean>(false)

	const [linkedOptions, setLinkedOptions] = useState<TLinkedEntity[]>([])

	const [loading, setLoading] = useState(false)
	const [loadingSearch, setLoadingSearch] = useState(false)
	const [open, setOpen] = useState(false)

	useEffect(() => {
		if (saving)
			handleSave()
	}, [saving])

	/**
	 * Event
	 * Find linked product's terroir
	 */
	useEffect(() => {
		const getLinked = async () => {
			setLoading(true)

			/**
			 * Liaisons
			 */
			await fetchApi<TLinkedEntity[]>(
				PARAMS.endpoints.getLinked(productId)
			)
				.then((res: TLinkedEntity[]) => {
					setLinkedOptions(Array.isArray(res) ?
						res.sort((a: TLinkedEntity, b: TLinkedEntity) => a.name.localeCompare(b.name))
						: [])
				})

			setLoadingSearch(false)
			setLoading(false)
		}

		getLinked()

	}, [])

	/**
	 * Methods
	 * Search options
	 */
	const handleSearch = useCallback(async (event: SyntheticEvent<Element, Event>, value: string) => {

		// @todo throttle request
		setLoadingSearch(true)

		/**
		 * Terroirs
		 */
		const results = await fetchApi<TBaseEntity[]>(
			PARAMS.endpoints.search(),
			undefined,
			{q: value}
		)

		setOptions(results)
		setLoadingSearch(false)

	}, [])

	/**
	 * Methods
	 * Save/Add/Remove row
	 */

	const updateLinked = useCallback((id: number, key: string, value: boolean | number) => {
		setLinkedOptions((linked) => {
			return linked.map((row) => {
				return row.id === id ? {
					...row,
					...{
						[key]: value
					}
				} : row
			})
		})

		setTouched(true)
	}, [])

	/**
	 * Methods
	 * Save/Add/Remove row
	 */

	const handleSelect = useCallback((event: SyntheticEvent, item: TBaseEntity | null) => {

		if (!item) return

		setLoadingSearch(true)

		fetchApi<TLinkedEntity>(
			PARAMS.endpoints.add(productId),
			{id: item.id},
			undefined,
			'PUT'
		)
			.then((res: TLinkedEntity) => {
				notify('Liaison ajoutée', {
					type: 'info',
					messageArgs: {smart_count: 1}
				})
				setLoadingSearch(false)
				setLinkedOptions((linked) => {
					return [
						...linked,
						...[res]
					]
				})
			})

	}, [])

	const handleSave = useCallback(() => {

		fetchApi(
			PARAMS.endpoints.save(productId),
			{linkedOptions},
			undefined,
			'PUT'
		)
			.then(() => {
				setTouched(false)
				notify('Liaisons sauvegardées', {
					type: 'info',
					messageArgs: {smart_count: 1}
				})
			})

	}, [linkedOptions])

	const handleRemove = useCallback((item: TLinkedEntity) => {
		if (confirm('Supprimer la liaison ?')) {
			fetchApi(
				PARAMS.endpoints.delete(productId),
				{id: item.id},
				undefined,
				'PUT'
			)
				.then(res => {
					if (res) {
						setLinkedOptions((linked) =>
							linked.filter(t => t.id !== item.id)
						)
						notify('Liaison supprimée', {
							type: 'info',
							messageArgs: {smart_count: 1}
						})
					}
				})
		}
	}, [])

	const linkedOptionsFiltered = useMemo(() => linkedOptions.filter((item: TLinkedEntity) =>
		normalizeString(item.product_id + ' -- ' + item.name).indexOf(normalizeString(filter)) >= 0
	), [filter, linkedOptions])

	return (
		<Box>
			<Typography variant="h6" gutterBottom>Produits liés</Typography>
			<Box sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				marginBottom: '16px',
				gap: '12px'
			}}>
				{linkedOptions.length > 0 &&
                    <TextField
                        sx={{flex: '1 1 50%'}}
                        variant="outlined"
                        label="Rechercher une liaison"
                        onChange={({target: {value}}) => setFilter(value)}
                    />
				}
				<Autocomplete
					id="search_terroirs"
					noOptionsText={'Aucun résultat'}
					renderInput={(params) =>
						<TextField variant="outlined" {...params} label="Ajouter une liaison" InputProps={{
							...params.InputProps,
							endAdornment: (
								<Fragment>
									{loadingSearch ?
										<CircularProgress sx={{marginRight: '30px'}} color="inherit" disableShrink
										                  size={20}/> : null}
									{params.InputProps.endAdornment}
								</Fragment>
							)
						}}/>
					}
					getOptionDisabled={(option) => !!linkedOptions.find(t => t.product_id === option.id)}
					size="small"
					sx={{flex: '1 1 50%'}}
					options={options}
					getOptionLabel={(option) => `${option.id.toString().padStart(4, '0')} -- ${option.name}`}
					open={open}
					onOpen={() => setOpen(true)}
					onClose={() => setOpen(false)}
					clearOnBlur={true}
					blurOnSelect={true}
					onInputChange={handleSearch}
					onChange={handleSelect}
				/>
			</Box>
			<Box sx={{display: 'flex', gap: '16px', flexDirection: 'column', alignItems: 'flex-start'}}>
				<Paper
					sx={{
						width: '100%',
						height: 300,
						overflow: 'hidden',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center'
					}}
				>
					{loading && <CircularProgress disableShrink size="5rem"/>}
					{!loading &&
                        <TableContainer sx={{height: 300}}>
                            <Table stickyHeader={true} aria-label="sticky table" size="small"
                                   sx={{height: 'max-content', border: 'black'}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{width: 300}}> Libellé </TableCell>
                                        <TableCell>Est 1932</TableCell>
                                        <TableCell>Est 1992</TableCell>
                                        <TableCell>Est 2022</TableCell>
										{enableTrust && <TableCell>Indice de confiance</TableCell>}
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
									{linkedOptionsFiltered.map((item: any) => (
										<TableRow key={item.id}>
											<TableCell>
												{`${item.product_id.toString().padStart(4, '0')} -- `}
												<LabelLinked resource="products" label={`${item.name}`}
												             id={item.product_id}/>
											</TableCell>
											<TableCell>
												<Checkbox
													id={item.id + '_1932'}
													onChange={({target}) => updateLinked(item.id, 'is1932', target.checked)}
													checked={item.is1932}
												/>
											</TableCell>
											<TableCell>
												<Checkbox
													id={item.id + '_1992'}
													onChange={({target}) => updateLinked(item.id, 'is1992', target.checked)}
													checked={item.is1992}
												/>
											</TableCell>
											<TableCell>
												<Checkbox
													id={item.id + '_2022'}
													onChange={({target}) => updateLinked(item.id, 'is2022', target.checked)}
													checked={item.is2022}
												/>
											</TableCell>
											{enableTrust &&
                                                <TableCell>
                                                    <TrustIndicator
                                                        value={item.trust}
                                                        onChange={(event: Event, value: number) => updateLinked(item.id, 'trust', value)}
                                                    />
                                                </TableCell>
											}
											<TableCell>
												<IconButton sx={{}} onClick={() => handleRemove(item)}>
													<RiCloseCircleFill/>
												</IconButton>
											</TableCell>
										</TableRow>
									))}
                                </TableBody>
                            </Table>
                        </TableContainer>
					}
				</Paper>
				<Button disabled={!touched} variant="contained" sx={{gap: '8px'}} onClick={handleSave}>
					<ContentSave height={18} width={18}/>Enregistrer
				</Button>
			</Box>
		</Box>
	)
}
